import { AlertCircle, CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { filesService } from "@/api/files";
import { FileType } from "@/api/files/files.types";
import { KB_ACCEPT } from "@/lib/uploadPolicy";
import { toast } from "@/components/ui";
import { extractErrorMessage } from "@/utils/errorUtils";

const SUCCESS_DISMISS_MS = 4_000;

type TUploadState = {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
};

type TCompanyKnowledgeUploadCardProps = {
  companyId: string;
  onUploadSuccess?: () => void;
};

export function CompanyKnowledgeUploadCard({ companyId, onUploadSuccess }: TCompanyKnowledgeUploadCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [uploads, setUploads] = useState<TUploadState[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const onUploadSuccessRef = useRef(onUploadSuccess);
  onUploadSuccessRef.current = onUploadSuccess;
  const isProcessingRef = useRef(false);

  const isUploading = uploads.some((u) => u.status === "uploading" || u.status === "pending");

  const processFiles = (fileList: File[]) => {
    const newUploads: TUploadState[] = fileList.map((file) => ({ file, status: "pending" as const }));
    setUploads((prev) => [...prev, ...newUploads]);
  };

  useEffect(() => {
    const pending = uploads.find((u) => u.status === "pending");
    if (!pending || !companyId || isProcessingRef.current) return;

    isProcessingRef.current = true;
    const file = pending.file;

    const run = async () => {
      setUploads((prev) => prev.map((u) => (u.file === file ? { ...u, status: "uploading" as const } : u)));

      try {
        const { data, error } = await filesService.uploadFile({
          file,
          fileType: FileType.COMPANY_DOCUMENT,
          companyId,
        });

        const fileId = data?.data?.id;
        if (error || !fileId) {
          const message = error ? await extractErrorMessage(error) : "Upload failed";
          setUploads((prev) =>
            prev.map((u) => (u.file === file ? { ...u, status: "error" as const, error: message } : u))
          );
          toast({
            title: "Upload failed",
            description: `${file.name}: ${message}`,
            variant: "destructive",
          });
          return;
        }

        setUploads((prev) => prev.map((u) => (u.file === file ? { ...u, status: "success" as const } : u)));
        toast({
          title: "File uploaded",
          description: `${file.name} was added to company knowledge.`,
        });
        onUploadSuccessRef.current?.();

        window.setTimeout(() => {
          setUploads((prev) => prev.filter((u) => u.file !== file));
        }, SUCCESS_DISMISS_MS);
      } finally {
        isProcessingRef.current = false;
      }
    };

    void run();
  }, [uploads, companyId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    processFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemove = (file: File) => {
    setUploads((prev) => prev.filter((u) => u.file !== file));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isUploading) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length) processFiles(files);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const activeUploadCount = uploads.filter((u) => u.status === "pending" || u.status === "uploading").length;

  return (
    <div className="space-y-3">
      {activeUploadCount > 0 && (
        <div
          className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/10 px-3 py-2.5 text-sm text-foreground"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
          <span>
            Uploading {activeUploadCount} file{activeUploadCount === 1 ? "" : "s"}…
          </span>
        </div>
      )}

      <div
        ref={dropZoneRef}
        className={`relative rounded-lg border border-dashed transition-colors ${
          isUploading
            ? "border-primary/30 bg-primary/5"
            : isDragging
              ? "border-primary bg-primary/10"
              : "border-border bg-muted"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-primary/10">
            <p className="text-sm font-medium text-primary">Drop here</p>
          </div>
        )}
        <div className="relative flex items-center justify-center gap-2 px-3 py-3">
          <Paperclip className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            accept={KB_ACCEPT}
          />
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={isUploading}
            className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
          >
            Choose files
          </button>
          <span className="text-sm text-muted-foreground">or drag & drop</span>
        </div>
      </div>

      {uploads.length > 0 && (
        <div className="space-y-2 rounded-lg border border-border bg-card p-3 shadow-sm">
          {uploads.map((upload, index) => (
            <div
              key={`${upload.file.name}-${index}`}
              className="space-y-1.5"
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{upload.file.name}</p>
                  {upload.status === "pending" && (
                    <p className="text-xs text-muted-foreground">Waiting to upload…</p>
                  )}
                  {upload.status === "uploading" && (
                    <p className="text-xs font-medium text-primary">Uploading…</p>
                  )}
                  {upload.status === "success" && (
                    <p className="flex items-center gap-1 text-xs font-medium text-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                      Uploaded successfully
                    </p>
                  )}
                  {upload.status === "error" && (
                    <p className="flex items-center gap-1 text-xs font-medium text-destructive">
                      <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                      {upload.error ?? "Upload failed"}
                    </p>
                  )}
                </div>
                {upload.status === "uploading" && (
                  <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-primary" />
                )}
                {upload.status === "success" && (
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-foreground" />
                )}
                {upload.status === "error" && (
                  <button
                    type="button"
                    onClick={() => handleRemove(upload.file)}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {(upload.status === "pending" || upload.status === "uploading") && (
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full w-full animate-pulse rounded-full bg-primary" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
